import { useState } from "react";
import { Table, Button, Modal, Form, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag, Divider } from "antd";
import { Plus, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import { useHRUsers, useTimesheets, useTimesheetCreate, useTimesheetUpdate, useTimesheetDelete, useTimesheetEntries, useTimesheetEntryCreate, useTimesheetEntryDelete } from "../../../../api-hooks/hr";

const TS_STATUS_COLORS: Record<string, string> = { draft: "default", submitted: "blue", approved: "green", rejected: "red", paid: "purple" };
const WORK_TYPES = ["regular","overtime","travel","training","other"];

export default function TimesheetsTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [entryForm] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [entriesModal, setEntriesModal] = useState<{ open: boolean; ts: any }>({ open: false, ts: null });
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string | undefined>();

  const { data: users = [] } = useHRUsers(cid);
  const { data: tsData, isLoading } = useTimesheets(cid, { page, limit: 50, status: filterStatus });
  const rows = (tsData as any)?.data ?? [];
  const total = (tsData as any)?.total ?? 0;

  const { data: entries = [] } = useTimesheetEntries(cid, entriesModal.ts?.id);
  const tsId = entriesModal.ts?.id;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useTimesheetCreate(cid, cb("Created.", () => setModal({ open: false, rec: null })));
  const upd = useTimesheetUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const del = useTimesheetDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const entryCreate = useTimesheetEntryCreate(cid, tsId, cb("Entry added.", () => entryForm.resetFields()));
  const entryDel = useTimesheetEntryDelete(cid, tsId, { onSuccess: () => msgApi.success("Entry deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Week Start", dataIndex: "week_start", key: "ws", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Week End", dataIndex: "week_end", key: "we", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Total Hrs", dataIndex: "total_hours", key: "th" },
    { title: "Overtime", dataIndex: "overtime_hours", key: "ot" },
    { title: "Status", dataIndex: "status", key: "st", render: (v: string) => <Tag color={TS_STATUS_COLORS[v] ?? "default"}>{v}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" icon={<ChevronDown size={12} />} onClick={() => setEntriesModal({ open: true, ts: r })}>Entries</Button>
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue({ ...r, week_start: r.week_start ? dayjs(r.week_start) : null, week_end: r.week_end ? dayjs(r.week_end) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const entryCols = [
    { title: "Date", dataIndex: "entry_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Start", dataIndex: "start_time", key: "s" },
    { title: "End", dataIndex: "end_time", key: "e" },
    { title: "Break (min)", dataIndex: "break_mins", key: "b" },
    { title: "Hours", dataIndex: "hours_worked", key: "h" },
    { title: "Type", dataIndex: "work_type", key: "wt" },
    { title: "Rate", dataIndex: "hourly_rate", key: "hr", render: (v: number) => v ? `£${v}` : "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => entryDel.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <Select allowClear placeholder="Filter by status" style={{ width: 160 }} onChange={v => { setFilterStatus(v); setPage(1); }} options={["draft","submitted","approved","rejected","paid"].map(v => ({ value: v, label: v }))} />
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ status: "draft", total_hours: 0, overtime_hours: 0 }); }} style={{ background: "#4c1d95" }}>New Timesheet</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={cols} loading={isLoading} size="small" pagination={{ current: page, pageSize: 50, total, onChange: setPage }} />

      {/* Timesheet modal */}
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Timesheet" : "New Timesheet"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, week_start: vals.week_start?.format("YYYY-MM-DD"), week_end: vals.week_end?.format("YYYY-MM-DD") };
          if (modal.rec) upd.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[])?.map(u => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="week_start" label="Week Start" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="week_end" label="Week End" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="status" label="Status"><Select options={["draft","submitted","approved","rejected","paid"].map(v => ({ value: v, label: v }))} /></Form.Item>
            <Form.Item name="total_hours" label="Total Hours"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
            <Form.Item name="overtime_hours" label="Overtime Hrs"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || upd.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Entries modal */}
      <Modal open={entriesModal.open} onCancel={() => setEntriesModal({ open: false, ts: null })} footer={null} destroyOnClose title={`Entries — ${entriesModal.ts?.user_name} (${entriesModal.ts?.week_start})`} width={700}>
        <Table rowKey="id" dataSource={entries as any[]} columns={entryCols} size="small" pagination={false} className="mb-4" />
        <Divider plain>Add Entry</Divider>
        <Form form={entryForm} layout="vertical" size="small" onFinish={(vals) => {
          entryCreate.mutate({ ...vals, user_id: entriesModal.ts?.user_id, start_time: vals.start_time?.format("HH:mm:ss"), end_time: vals.end_time?.format("HH:mm:ss"), entry_date: vals.entry_date?.format("YYYY-MM-DD") });
        }}>
          <div className="grid grid-cols-4 gap-x-3">
            <Form.Item name="entry_date" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="start_time" label="Start" rules={[{ required: true }]}><DatePicker.TimePicker className="w-full" format="HH:mm" /></Form.Item>
            <Form.Item name="end_time" label="End" rules={[{ required: true }]}><DatePicker.TimePicker className="w-full" format="HH:mm" /></Form.Item>
            <Form.Item name="break_mins" label="Break (min)"><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="hours_worked" label="Hours" rules={[{ required: true }]}><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
            <Form.Item name="work_type" label="Type" initialValue="regular"><Select options={WORK_TYPES.map(v => ({ value: v, label: v }))} /></Form.Item>
            <Form.Item name="hourly_rate" label="Rate (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <Button type="primary" htmlType="submit" size="small" style={{ background: "#4c1d95" }} loading={entryCreate.isPending}>Add Entry</Button>
        </Form>
      </Modal>
    </div>
  );
}
